import api from '@/services/api';
import { jobApi, type JobPosting } from '@/modules/job/services/job.api';
import { calendarApi } from '@/modules/calendar/services/calendar.api';
import type { SyncedCalendarEvent } from '@/modules/calendar/types/calendar.types';
import type { TaskItem } from '@/modules/shared/types/yc-demo';
import type { ActivityFeedEvent, ActivityIconName } from '../components/RecentActivityFeed';

// ─── Tasks (Plan 01 stub fallback) ────────────────────────────

/**
 * Plan 01 owns POST `/v2/tasks`. Until that ships, we attempt the real call
 * and fall back to typed mock data derived from real seeded jobs so the UI
 * still demos with realistic content. The mock is deterministic per-job.
 */
export async function fetchPendingTasks(jobs: JobPosting[]): Promise<TaskItem[]> {
    try {
        const response = await api.get<{ tasks: TaskItem[] }>('/v2/tasks', {
            params: { status: 'pending' },
        });
        if (Array.isArray(response.data?.tasks) && response.data.tasks.length > 0) {
            return response.data.tasks;
        }
    } catch {
        // Plan 01 hasn't shipped — fall through to mocks below.
    }

    return buildMockTasks(jobs);
}

const MOCK_CANDIDATES = [
    'Maya Chen',
    'Priya Sharma',
    'Marco Lin',
    'June Park',
    'Hiro Tanaka',
    'Aisha Khan',
    'Diego Alvarez',
    'Nora Hassan',
];

function buildMockTasks(jobs: JobPosting[]): TaskItem[] {
    if (jobs.length === 0) return [];

    const now = Date.now();
    const orgId = jobs[0].organizationId || 'org';
    const wsId = jobs[0].workspaceId || 'ws';

    const candidates = MOCK_CANDIDATES.slice(0, 4);
    const kinds: TaskItem['kind'][] = ['cv_review', 'cv_review', 'ai_interview_review', 'cv_review'];
    const priorities: TaskItem['priority'][] = ['high', 'high', 'normal', 'high'];
    // Realistic dueAt offsets — first 2 are due in hours, last 2 are tomorrow / overdue
    const dueDeltasHours = [4, 8, 26, -3];

    return candidates.map((candidate, idx) => {
        const job = jobs[idx % jobs.length];
        return {
            _id: `mock-task-${idx}-${job._id}`,
            organizationId: orgId,
            workspaceId: wsId,
            assigneeId: 'me',
            kind: kinds[idx],
            applicationId: `mock-app-${idx}-${job._id}`,
            candidateName: candidate,
            jobId: job._id,
            jobTitle: job.title,
            status: 'pending' as const,
            priority: priorities[idx],
            dueAt: new Date(now + dueDeltasHours[idx] * 3600_000).toISOString(),
            createdAt: new Date(now - (idx + 1) * 7200_000).toISOString(),
        };
    });
}

// ─── Calendar ─────────────────────────────────────────────────

function startOfToday() {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d.toISOString();
}

function endOfToday() {
    const d = new Date();
    d.setHours(23, 59, 59, 999);
    return d.toISOString();
}

export async function fetchTodaysEvents(): Promise<SyncedCalendarEvent[]> {
    try {
        const response = await calendarApi.fetchEvents(startOfToday(), endOfToday());
        const events = Array.isArray(response.data) ? response.data : [];
        return events
            .filter((e) => e.status !== 'cancelled')
            .filter((e) => new Date(e.endDateTime).getTime() > Date.now())
            .sort((a, b) => new Date(a.startDateTime).getTime() - new Date(b.startDateTime).getTime());
    } catch {
        return [];
    }
}

// ─── Jobs ─────────────────────────────────────────────────────

export async function fetchActiveJobs(): Promise<JobPosting[]> {
    const response = await jobApi.listJobs({ status: 'open', limit: 8 });
    const open = response.data?.results ?? [];
    if (open.length > 0) return open;
    // Fall back to whatever exists if nothing is "open"
    const all = await jobApi.listJobs({ limit: 8 });
    return all.data?.results ?? [];
}

// ─── Activity feed composition ────────────────────────────────

export function composeActivityFeed(jobs: JobPosting[], tasks: TaskItem[]): ActivityFeedEvent[] {
    const events: ActivityFeedEvent[] = [];
    const now = Date.now();

    if (jobs.length === 0) return [];

    const primary = jobs[0];
    const secondary = jobs[1] ?? primary;
    const tertiary = jobs[2] ?? primary;

    // Compose 6–8 plausible recent events from real seeded entities.
    events.push({
        id: 'a-1',
        iconName: 'sourcing',
        text: `Lantern sourced *${30 + (primary._id.charCodeAt(0) % 18)} candidates* for ${primary.title}.`,
        at: new Date(now - 12 * 60_000).toISOString(),
    });

    events.push({
        id: 'a-2',
        iconName: 'screening',
        text: `Screened *14 new applicants* against ${primary.title} rubric.`,
        at: new Date(now - 28 * 60_000).toISOString(),
    });

    if (tasks[0]) {
        events.push({
            id: 'a-3',
            iconName: 'human',
            text: `Flagged *${tasks[0].candidateName}* for your judgment on ${tasks[0].jobTitle}.`,
            at: tasks[0].createdAt,
        });
    }

    events.push({
        id: 'a-4',
        iconName: 'nurture',
        text: `Pre-drafted outreach for *${8 + (secondary._id.charCodeAt(0) % 9)} warm referrals* on ${secondary.title}.`,
        at: new Date(now - 84 * 60_000).toISOString(),
    });

    events.push({
        id: 'a-5',
        iconName: 'interview',
        text: `Scored ${tertiary.title} async interview: *3 candidates moved* to next stage.`,
        at: new Date(now - 142 * 60_000).toISOString(),
    });

    if (tasks[1]) {
        events.push({
            id: 'a-6',
            iconName: 'screening',
            text: `Drafted CV report for *${tasks[1].candidateName}* (${tasks[1].jobTitle}).`,
            at: tasks[1].createdAt,
        });
    }

    events.push({
        id: 'a-7',
        iconName: 'sourcing',
        text: `Mapped *6 adjacent companies* for ${primary.title} sourcing graph.`,
        at: new Date(now - 4 * 3600_000).toISOString(),
    });

    events.push({
        id: 'a-8',
        iconName: 'nurture',
        text: `Sent follow-up to *4 dormant prospects* on ${secondary.title}.`,
        at: new Date(now - 5 * 3600_000).toISOString(),
    });

    return events.slice(0, 8);
}

// ─── Last action timestamp ────────────────────────────────────

export function getLastActionAgoMs(jobs: JobPosting[], tasks: TaskItem[]): number {
    const candidates: number[] = [];
    if (jobs[0]?.updatedAt) candidates.push(Date.now() - new Date(jobs[0].updatedAt).getTime());
    if (tasks[0]?.createdAt) candidates.push(Date.now() - new Date(tasks[0].createdAt).getTime());
    if (candidates.length === 0) return 4 * 60_000; // 4m default
    return Math.min(...candidates.filter((n) => n > 0));
}

export type { ActivityFeedEvent, ActivityIconName };
